import React, { useEffect, useState } from "react";
import clsx from "clsx";
import snakeCase from "lodash/snakeCase";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import OrderTopIcon from "@mui/icons-material/ArrowUpward";
import OrderDownIcon from "@mui/icons-material/ArrowDownward";

import { isReactComponent } from "../../../utils/detect";
import ResizeBorder from "./ResizeBorder";
import { MIN_CELL_WIDTH } from "../utils";
import TableCell from "../TableCell";

export const MIN_WIDTH = 200;
export const MIN_HEIGHT = 60;

const styles = {
  header: {
    position: "sticky",
    top: 0,
    display: "block",
    zIndex: 4,
    backgroundColor: "common.white",
  },
  row: {
    display: "flex",
  },
  cellContent: {
    justifyContent: "center",
    backgroundColor: "common.white",
    overflow: "hidden",
    width: "100%",
    position: "relative",
    "&:hover .sort-button": {
      opacity: 1,
      visibility: "visible",
    },
  },
  label: {
    marginRight: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  sortButtonHidden: {
    padding: "0",
    opacity: 0,
    visibility: "hidden",
  },
  sortButtonVisible: {
    padding: "0",
    opacity: 1,
    visibility: "visible",
  },
};

export default function TableHeader({
  fields: initHeaders,
  order: initOrder,
  loading,
  onChange,
  fieldsProps = {},
  className,
  ...props
}) {
  const [fields, setFields] = useState(initHeaders);
  const [order, setOrder] = useState(initOrder || {});
  const [disabledDragger, setDisabledDragger] = useState(false);

  const handleUpdate = newFields => onChange({ fields: newFields });

  useEffect(() => {
    setFields(initHeaders);
  }, [initHeaders]);

  useEffect(() => {
    setOrder(initOrder || {});
  }, [initOrder]);

  const onBorderResizing = (index, resizeWidth) => {
    setFields(fields =>
      Object.assign([], fields, {
        [index]: {
          ...fields[index],
          width: Math.max(
            MIN_CELL_WIDTH,
            (fields[index].width || MIN_WIDTH) + resizeWidth
          ),
        },
      })
    );
  };

  const onBorderStopReisizing = () => {
    handleUpdate(fields);
  };

  const toggleSort = fieldId => () => {
    const id = snakeCase(fieldId);

    if (loading) return;
    if (!order[id]) handleSortDesc(id);
    if (order[id] === "desc") handleSortAsc(id);
    if (order[id] === "asc") handleSortDesc(id);
  };

  const handleSortAsc = fieldId => {
    const newOrder = { [fieldId]: "asc" };
    setOrder(newOrder);
    onChange({ order: newOrder });
  };

  const handleSortDesc = fieldId => {
    const newOrder = { [fieldId]: "desc" };
    setOrder(newOrder);
    onChange({ order: newOrder });
  };

  return (
    <Box className={clsx(className, "resizableWrap")} sx={styles.header} {...props}>
      <Box data-test-id={"field-row"} sx={styles.row}>
        {fields.map((currentField, index) => {
          const field = {
            ...currentField,
            ...(fieldsProps[currentField.id] || {}),
          };

          const isAsc = order[snakeCase(field.id)] === "asc";
          const isDesc = order[snakeCase(field.id)] === "desc";
          const isSorted = isAsc || isDesc;

          return (
            <TableCell
              id={field.id}
              key={field.id}
              index={index}
              fields={fields}
              field={field}
              className="pro-table-cell"
              contentProps={{
                sx: styles.cellContent,
              }}
              style={{
                height: MIN_HEIGHT,
                width: Math.max(MIN_CELL_WIDTH, field.width || MIN_WIDTH),
                minWidth: Math.max(MIN_CELL_WIDTH, field.width || MIN_WIDTH),
              }}
            >
              <Box display="flex">
                <Box sx={styles.label}>
                  {field.label && isReactComponent(field.label) ? <field.label /> : field.label}
                </Box>
              </Box>

              {!field.disableOrder && (
                <Box>
                  {!isSorted && (
                    <IconButton
                      onClick={toggleSort(field.id)}
                      className="sort-button"
                      sx={styles.sortButtonHidden}
                    >
                      <OrderDownIcon color="disabled" fontSize="small" />
                    </IconButton>
                  )}

                  {isDesc && (
                    <IconButton
                      onClick={toggleSort(field.id)}
                      className="sort-button active"
                      sx={styles.sortButtonVisible}
                    >
                      <OrderDownIcon fontSize="small" />
                    </IconButton>
                  )}

                  {isAsc && (
                    <IconButton
                      onClick={toggleSort(field.id)}
                      className="sort-button active"
                      sx={styles.sortButtonVisible}
                    >
                      <OrderTopIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              <ResizeBorder
                onMouseMove={() => setDisabledDragger(true)}
                onMouseOut={() => setDisabledDragger(false)}
                onStopReisizing={onBorderStopReisizing}
                onResizing={onBorderResizing}
                index={index}
              />
            </TableCell>
          );
        })}
      </Box>
    </Box>
  );
}
